import {
  Badge,
  BrandMark,
  Button,
  Card,
  Icon,
  PageHeader,
  TwoFactorChallenge,
  useModalDialog,
} from "@kindtutor/web-ui";
import type { HqPermission, HqRole } from "@kindtutor/api-contract";
import {
  fetchAccessContext,
  getAuthErrorMessage,
  isSafeReturnPath,
} from "@kindtutor/auth-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  Link,
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { apiBaseURL, apiFetch, authClient } from "./auth-client";
import {
  fetchHqNotifications,
  markAllHqNotificationsRead,
  markHqNotificationRead,
  searchHq,
} from "./api";
import { formatLastSeen } from "./formatters";

const Dashboard = lazy(() => import("./routes/DashboardRoute"));
const Fleet = lazy(() => import("./routes/FleetRoute"));
const Releases = lazy(() => import("./routes/ReleasesRoute"));
const DeviceDetails = lazy(() => import("./routes/DeviceDetailsRoute"));
const Incidents = lazy(() => import("./routes/IncidentsRoute"));
const Billing = lazy(() => import("./routes/BillingRoute"));
const Audit = lazy(() => import("./routes/AuditRoute"));
const Settings = lazy(() => import("./routes/SettingsRoute"));
const Pilots = lazy(() => import("./routes/PilotsRoute"));
const Sales = lazy(() => import("./routes/SalesRoute"));
const Schools = lazy(() => import("./routes/SchoolsRoute"));
const SchoolDetails = lazy(() => import("./routes/SchoolDetailsRoute"));
const SchoolConversations = lazy(
  () => import("./routes/SchoolConversationsRoute"),
);

const nav = [
  ["/", "Centrum dowodzenia", "layout-dashboard", "dashboard.read"],
  ["/schools", "Szkoły", "school", "schools.read"],
  ["/sales", "Sprzedaż", "trending-up", "sales.read"],
  ["/pilots", "Pilotaże", "rocket", "pilots.read"],
  ["/fleet", "Flota urządzeń", "monitor", "fleet.read"],
  ["/releases", "Wydania station", "rocket", "fleet.read"],
  ["/support", "Wsparcie", "life-buoy", "support.read"],
  ["/billing", "Rozliczenia", "credit-card", "billing.read"],
  ["/audit", "Dziennik audytu", "scroll-text", "audit.read"],
  ["/settings", "Ustawienia platformy", "settings", "settings.read"],
] as const;

const hqRoleLabels: Record<HqRole, string> = {
  superadmin: "Superadministrator",
  support: "Wsparcie",
  sales: "Sprzedaż",
  operations: "Operacje",
  finance: "Finanse",
  read_only: "Tylko odczyt",
};

function useSecureSignOut() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return async () => {
    queryClient.clear();
    await authClient.signOut();
    void navigate("/login", { replace: true });
  };
}

function useClearCacheOnIdentityChange(userId: string | undefined) {
  const queryClient = useQueryClient();
  const previousUserId = useRef<string | null>(null);

  useEffect(() => {
    const nextUserId = userId ?? null;
    if (previousUserId.current && previousUserId.current !== nextUserId) {
      queryClient.clear();
    }
    previousUserId.current = nextUserId;
  }, [queryClient, userId]);
}

function Shell({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const secureSignOut = useSecureSignOut();
  const [open, setOpen] = useState(false);
  const [notices, setNotices] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const sidebarRef = useModalDialog<HTMLElement>({
    open,
    onClose: () => setOpen(false),
  });
  const navigate = useNavigate();
  const { data: session } = authClient.useSession();
  const access = useQuery({
    queryKey: ["access-context", session?.user.id],
    queryFn: () => fetchAccessContext(apiBaseURL, apiFetch),
    enabled: Boolean(session),
    staleTime: 30_000,
  });
  const permissions = access.data?.hqPermissions
    ? new Set<HqPermission>(access.data.hqPermissions)
    : null;
  const userInitials = (session?.user.name ?? "HQ")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
  const searchResults = useQuery({
    queryKey: ["hq-search", search.trim()],
    queryFn: () => searchHq(search.trim()),
    enabled: search.trim().length >= 2,
  });
  const notifications = useQuery({
    queryKey: ["hq-notifications"],
    queryFn: fetchHqNotifications,
    refetchInterval: 30_000,
  });
  const unreadNotifications =
    notifications.data?.notifications.filter(
      (notification) => !notification.readAt,
    ) ?? [];
  const markNotification = useMutation({
    mutationFn: markHqNotificationRead,
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: ["hq-notifications"] });
      const previous = queryClient.getQueryData<
        Awaited<ReturnType<typeof fetchHqNotifications>>
      >(["hq-notifications"]);
      queryClient.setQueryData<
        Awaited<ReturnType<typeof fetchHqNotifications>>
      >(["hq-notifications"], (current) =>
        current
          ? {
              ...current,
              notifications: current.notifications.map((notification) =>
                notification.id === notificationId && !notification.readAt
                  ? { ...notification, readAt: new Date().toISOString() }
                  : notification,
              ),
              unreadCount: Math.max(0, current.unreadCount - 1),
            }
          : current,
      );
      return { previous };
    },
    onError: (_error, _notificationId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["hq-notifications"], context.previous);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["hq-notifications"] });
    },
  });
  const markAllNotifications = useMutation({
    mutationFn: markAllHqNotificationsRead,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["hq-notifications"] });
      const previous = queryClient.getQueryData<
        Awaited<ReturnType<typeof fetchHqNotifications>>
      >(["hq-notifications"]);
      const readAt = new Date().toISOString();
      queryClient.setQueryData<
        Awaited<ReturnType<typeof fetchHqNotifications>>
      >(["hq-notifications"], (current) =>
        current
          ? {
              ...current,
              notifications: current.notifications.map((notification) => ({
                ...notification,
                readAt: notification.readAt ?? readAt,
              })),
              unreadCount: 0,
            }
          : current,
      );
      return { previous };
    },
    onError: (_error, _values, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["hq-notifications"], context.previous);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["hq-notifications"] });
    },
  });
  useEffect(() => {
    const focusSearch = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", focusSearch);
    return () => window.removeEventListener("keydown", focusSearch);
  }, []);
  return (
    <div className="hq-shell">
      <a className="skip-link" href="#main">
        Przejdź do treści
      </a>
      <aside ref={sidebarRef} className={`hq-sidebar ${open ? "is-open" : ""}`}>
        <div className="hq-brand">
          <BrandMark className="hq-brand-mark" />
          <strong>KindTutor</strong>
          <Badge tone="info">HQ</Badge>
        </div>
        <nav aria-label="Nawigacja administratora">
          {nav
            .filter(([, , , permission]) =>
              permissions ? permissions.has(permission) : true,
            )
            .map(([to, label, icon]) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                onClick={() => setOpen(false)}
              >
                <span className="nav-icon" aria-hidden="true">
                  <Icon name={icon} size={20} />
                </span>
                {label}
              </NavLink>
            ))}
        </nav>
      </aside>
      {open ? (
        <button
          className="scrim"
          aria-label="Zamknij menu"
          onClick={() => setOpen(false)}
        />
      ) : null}
      <section className="hq-workspace">
        <header className="hq-topbar">
          <button
            className="hq-icon menu"
            aria-label={open ? "Zamknij menu" : "Otwórz menu"}
            aria-expanded={open}
            onClick={() => setOpen(true)}
          >
            <Icon name="menu" size={21} />
          </button>
          <div className="hq-search">
            <Icon name="search" size={21} />
            <input
              ref={searchRef}
              type="search"
              aria-label="Szukaj w panelu HQ"
              placeholder="Szukaj szkoły, kontaktu, urządzenia..."
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
            />
            {search.trim().length >= 2 ? (
              <Card className="hq-search-results">
                {searchResults.isPending ? <p role="status">Szukamy…</p> : null}
                {searchResults.isError ? (
                  <p role="alert">Nie udało się wyszukać.</p>
                ) : null}
                {searchResults.data?.results.map((result) => (
                  <button
                    type="button"
                    key={`${result.type}-${result.id}`}
                    onClick={() => {
                      setSearch("");
                      void navigate(result.path);
                    }}
                  >
                    <span>
                      {result.type === "school"
                        ? "Szkoła"
                        : result.type === "device"
                          ? "Urządzenie"
                          : result.type === "lead"
                            ? "Lead"
                            : "Incydent"}
                    </span>
                    <strong>{result.title}</strong>
                    <small>{result.subtitle}</small>
                  </button>
                ))}
                {searchResults.data?.results.length === 0 ? (
                  <p>Brak wyników dla „{search.trim()}”.</p>
                ) : null}
              </Card>
            ) : null}
          </div>
          <button
            className="hq-icon"
            aria-label={`Powiadomienia, ${notifications.data?.unreadCount ?? 0} nowych`}
            onClick={() => {
              setSearch("");
              setProfileOpen(false);
              setNotices(!notices);
            }}
          >
            <Icon name="bell" size={21} />
            {(notifications.data?.unreadCount ?? 0) > 0 ? (
              <span className="dot">{notifications.data?.unreadCount}</span>
            ) : null}
          </button>
          {notices ? (
            <Card className="hq-notifications">
              <div>
                <strong>Powiadomienia</strong>
                <button
                  disabled={
                    !notifications.data?.unreadCount ||
                    markAllNotifications.isPending
                  }
                  onClick={() => markAllNotifications.mutate()}
                >
                  Przeczytaj wszystkie
                </button>
              </div>
              {notifications.isPending ? (
                <p role="status">Wczytujemy powiadomienia…</p>
              ) : null}
              {notifications.isError ? (
                <p role="alert">Nie udało się wczytać powiadomień.</p>
              ) : null}
              {markNotification.isError || markAllNotifications.isError ? (
                <p role="alert">
                  Nie udało się oznaczyć powiadomienia jako przeczytanego.
                </p>
              ) : null}
              {unreadNotifications.map((notification) => (
                <button
                  type="button"
                  className={notification.readAt ? "is-read" : ""}
                  key={notification.id}
                  onClick={() => {
                    if (!notification.readAt) {
                      markNotification.mutate(notification.id);
                    }
                    setNotices(false);
                    void navigate(notification.path);
                  }}
                >
                  <span>
                    {notification.severity === "critical" ? "!" : "✓"}
                  </span>
                  <div>
                    <strong>{notification.title}</strong>
                    <small>{notification.body}</small>
                    <small>{formatLastSeen(notification.createdAt)}</small>
                  </div>
                </button>
              ))}
              {unreadNotifications.length === 0 ? (
                <p>Brak nowych powiadomień.</p>
              ) : null}
            </Card>
          ) : null}
          <button
            className="hq-profile-button"
            aria-label="Menu użytkownika"
            aria-expanded={profileOpen}
            onClick={() => {
              setNotices(false);
              setSearch("");
              setProfileOpen((visible) => !visible);
            }}
          >
            <span>{userInitials}</span>
            <span className="hq-profile-copy">
              <strong>{session?.user.name ?? "Administrator"}</strong>
              <small>
                {access.data?.hqRole
                  ? hqRoleLabels[access.data.hqRole]
                  : "Administrator HQ"}
              </small>
            </span>
          </button>
          {profileOpen ? (
            <Card className="hq-profile-popover">
              <strong>{session?.user.name ?? "Administrator"}</strong>
              <span>{session?.user.email}</span>
              <Link to="/change-password" onClick={() => setProfileOpen(false)}>
                Zmień hasło
              </Link>
              <button
                onClick={() => {
                  void (async () => {
                    await secureSignOut();
                    setProfileOpen(false);
                  })();
                }}
              >
                Wyloguj się
              </button>
            </Card>
          ) : null}
        </header>
        <main id="main" className="hq-content" tabIndex={-1}>
          {children}
        </main>
      </section>
    </div>
  );
}

function Login() {
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const requestedPath = (location.state as { from?: unknown } | null)?.from;
  const returnTo = isSafeReturnPath(requestedPath) ? requestedPath : "/";

  if (!sessionPending && session?.user.role === "admin") {
    return <Navigate to={returnTo} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setPending(true);
    const data = new FormData(event.currentTarget);
    const result = await authClient.signIn.email({
      email: String(data.get("email") ?? "").trim(),
      password: String(data.get("password") ?? ""),
      rememberMe: true,
    });
    setPending(false);
    if (result.error) {
      setError(getAuthErrorMessage(result.error));
      return;
    }
    void navigate(returnTo, { replace: true });
  }

  return (
    <main className="hq-auth">
      <Card>
        <div className="hq-brand">
          <BrandMark className="hq-brand-mark" />
          <strong>KindTutor</strong>
          <Badge tone="info">HQ</Badge>
        </div>
        <h1>Panel administratora</h1>
        <p>Zaloguj się kontem z dostępem globalnym.</p>
        <form onSubmit={(event) => void handleSubmit(event)}>
          <label>
            E-mail
            <input name="email" type="email" autoComplete="email" required />
          </label>
          <label>
            Hasło
            <input
              name="password"
              type="password"
              autoComplete="current-password"
              required
            />
          </label>
          {error ? (
            <p className="auth-message auth-message--error" role="alert">
              {error}
            </p>
          ) : null}
          <Link to="/forgot-password">Nie pamiętam hasła</Link>
          <Button type="submit" disabled={pending || sessionPending}>
            {pending ? "Logowanie…" : "Zaloguj się"}
          </Button>
        </form>
      </Card>
    </main>
  );
}

function TwoFactorLogin() {
  const navigate = useNavigate();
  const verify = async (
    operation: Promise<{ error: { code?: string; status?: number } | null }>,
  ) => {
    const result = await operation;
    if (result.error) throw new Error(getAuthErrorMessage(result.error));
  };
  return (
    <main className="hq-auth">
      <Card>
        <TwoFactorChallenge
          onVerifyCode={(code, trustDevice) =>
            verify(authClient.twoFactor.verifyTotp({ code, trustDevice }))
          }
          onVerifyBackupCode={(code, trustDevice) =>
            verify(
              authClient.twoFactor.verifyBackupCode({
                code,
                trustDevice,
              }),
            )
          }
          onVerified={() => void navigate("/", { replace: true })}
        />
      </Card>
    </main>
  );
}

function ForgotPassword() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setPending(true);
    const data = new FormData(event.currentTarget);
    const result = await authClient.requestPasswordReset({
      email: String(data.get("email") ?? "").trim(),
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setPending(false);
    if (result.error) {
      setError(getAuthErrorMessage(result.error));
      return;
    }
    setSent(true);
  }

  return (
    <main className="hq-auth">
      <Card>
        <div className="hq-brand">
          <BrandMark className="hq-brand-mark" />
          <strong>KindTutor</strong>
          <Badge tone="info">HQ</Badge>
        </div>
        <h1>Przypomnij hasło</h1>
        <p>Wyślemy bezpieczny link na adres przypisany do konta.</p>
        {sent ? (
          <div className="auth-success" role="status">
            <strong>Sprawdź swoją skrzynkę</strong>
            <p>
              Jeśli konto istnieje, wysłaliśmy wiadomość z dalszymi krokami.
            </p>
          </div>
        ) : (
          <form onSubmit={(event) => void handleSubmit(event)}>
            <label>
              E-mail
              <input name="email" type="email" autoComplete="email" required />
            </label>
            {error ? (
              <p className="auth-message auth-message--error" role="alert">
                {error}
              </p>
            ) : null}
            <Button type="submit" disabled={pending}>
              {pending ? "Wysyłanie…" : "Wyślij link"}
            </Button>
          </form>
        )}
        <Link className="back-link" to="/login">
          ← Wróć do logowania
        </Link>
      </Card>
    </main>
  );
}

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [pending, setPending] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;
    const data = new FormData(event.currentTarget);
    const password = String(data.get("password") ?? "");
    if (password !== String(data.get("confirmation") ?? "")) {
      setError("Hasła nie są takie same.");
      return;
    }
    setPending(true);
    setError("");
    const result = await authClient.resetPassword({
      newPassword: password,
      token,
    });
    setPending(false);
    if (result.error) {
      setError(getAuthErrorMessage(result.error));
      return;
    }
    setDone(true);
  }

  return (
    <main className="hq-auth">
      <Card>
        <div className="hq-brand">
          <BrandMark className="hq-brand-mark" />
          <strong>KindTutor</strong>
          <Badge tone="info">HQ</Badge>
        </div>
        <h1>Ustaw nowe hasło</h1>
        {!token ? (
          <p className="auth-message auth-message--error" role="alert">
            Ten link jest nieprawidłowy. Poproś o nową wiadomość.
          </p>
        ) : done ? (
          <div className="auth-success" role="status">
            <strong>Hasło zostało zmienione</strong>
            <Link to="/login">Przejdź do logowania</Link>
          </div>
        ) : (
          <form onSubmit={(event) => void handleSubmit(event)}>
            <label>
              Nowe hasło
              <input
                name="password"
                type="password"
                minLength={12}
                maxLength={128}
                autoComplete="new-password"
                required
              />
              <small>Co najmniej 12 znaków.</small>
            </label>
            <label>
              Powtórz nowe hasło
              <input
                name="confirmation"
                type="password"
                minLength={12}
                maxLength={128}
                autoComplete="new-password"
                required
              />
            </label>
            {error ? (
              <p className="auth-message auth-message--error" role="alert">
                {error}
              </p>
            ) : null}
            <Button type="submit" disabled={pending}>
              {pending ? "Zapisywanie…" : "Zapisz nowe hasło"}
            </Button>
          </form>
        )}
      </Card>
    </main>
  );
}

function ChangePassword() {
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [pending, setPending] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const newPassword = String(data.get("newPassword") ?? "");
    if (newPassword !== String(data.get("confirmation") ?? "")) {
      setError("Nowe hasła nie są takie same.");
      return;
    }
    setPending(true);
    setDone(false);
    setError("");
    const result = await authClient.changePassword({
      currentPassword: String(data.get("currentPassword") ?? ""),
      newPassword,
      revokeOtherSessions: true,
    });
    setPending(false);
    if (result.error) {
      setError(getAuthErrorMessage(result.error));
      return;
    }
    form.reset();
    setDone(true);
  }

  return (
    <>
      <PageHeader
        title="Zmień hasło"
        description="Po zmianie hasła pozostałe urządzenia zostaną wylogowane."
      />
      <Card className="password-card">
        <form
          className="password-form"
          onSubmit={(event) => void handleSubmit(event)}
        >
          <label>
            Obecne hasło
            <input
              name="currentPassword"
              type="password"
              autoComplete="current-password"
              required
            />
          </label>
          <label>
            Nowe hasło
            <input
              name="newPassword"
              type="password"
              minLength={12}
              maxLength={128}
              autoComplete="new-password"
              required
            />
          </label>
          <label>
            Powtórz nowe hasło
            <input
              name="confirmation"
              type="password"
              minLength={12}
              maxLength={128}
              autoComplete="new-password"
              required
            />
          </label>
          {error ? (
            <p className="auth-message auth-message--error" role="alert">
              {error}
            </p>
          ) : null}
          {done ? (
            <p className="auth-message auth-message--success" role="status">
              Hasło zostało zmienione.
            </p>
          ) : null}
          <Button type="submit" disabled={pending}>
            {pending ? "Zapisywanie…" : "Zmień hasło"}
          </Button>
        </form>
      </Card>
    </>
  );
}

function RequireAdmin({ children }: { children: ReactNode }) {
  const secureSignOut = useSecureSignOut();
  const { data: session, isPending } = authClient.useSession();
  useClearCacheOnIdentityChange(session?.user.id);
  const access = useQuery({
    queryKey: ["access-context", session?.user.id],
    queryFn: () => fetchAccessContext(apiBaseURL, apiFetch),
    enabled: Boolean(session),
    staleTime: 30_000,
  });
  const location = useLocation();
  if (isPending) {
    return (
      <main className="auth-loading" aria-live="polite">
        <span className="auth-spinner" />
        <p>Sprawdzamy dostęp…</p>
      </main>
    );
  }
  if (!session) {
    return (
      <Navigate
        to="/login"
        state={{ from: `${location.pathname}${location.search}` }}
        replace
      />
    );
  }
  if (access.isPending) {
    return (
      <main className="auth-loading" aria-live="polite">
        <span className="auth-spinner" />
        <p>Sprawdzamy rolę administratora…</p>
      </main>
    );
  }
  if (access.isError) {
    return (
      <main className="hq-auth">
        <Card>
          <h1>Nie udało się sprawdzić dostępu</h1>
          <p>Odśwież stronę lub spróbuj ponownie za chwilę.</p>
          <Button onClick={() => void access.refetch()}>
            Spróbuj ponownie
          </Button>
        </Card>
      </main>
    );
  }
  if (session.user.role !== "admin" || !access.data?.hqAccess) {
    return (
      <main className="hq-auth">
        <Card>
          <h1>Brak dostępu do HQ</h1>
          <p>To konto nie ma uprawnień administratora globalnego.</p>
          <Button
            onClick={() => {
              void (async () => {
                await secureSignOut();
              })();
            }}
          >
            Zaloguj się innym kontem
          </Button>
        </Card>
      </main>
    );
  }
  return children;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/two-factor" element={<TwoFactorLogin />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="*"
        element={
          <RequireAdmin>
            <Shell>
              <Suspense fallback={<p role="status">Wczytywanie widoku…</p>}>
                <Routes>
                  <Route index element={<Dashboard />} />
                  <Route path="schools" element={<Schools />} />
                  <Route path="schools/:schoolId" element={<SchoolDetails />} />
                  <Route
                    path="schools/:schoolId/conversations"
                    element={<SchoolConversations />}
                  />
                  <Route path="sales" element={<Sales />} />
                  <Route path="pilots" element={<Pilots />} />
                  <Route path="fleet" element={<Fleet />} />
                  <Route path="fleet/:deviceId" element={<DeviceDetails />} />
                  <Route path="releases" element={<Releases />} />
                  <Route path="support" element={<Incidents />} />
                  <Route path="billing" element={<Billing />} />
                  <Route path="audit" element={<Audit />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="change-password" element={<ChangePassword />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </Shell>
          </RequireAdmin>
        }
      />
    </Routes>
  );
}
